import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Translate } from "react-localize-redux";
import { AnimatePresence } from "framer-motion";

import {
  selectOrderBarbershop,
  selectOrderServices,
  selectOrderExtraServices,
  editOrder,
  sendOrder,
  selectOrderSending,
  selectOrderBarber,
} from "./orderSlice";
import { analytics } from "../../utils/Analytics";
import { isEmptyObject } from "../../utils/Helpers";
import { useServiceExtraServices } from "./use-service-extra-services";
import { ReactComponent as IconDownIndicator } from "../../assets/images/icon-down-indicator.svg";
import FadeIn from "../animations/FadeIn";
import useOnScreen from "../../utils/use-on-screen";
import StaticGoogleMapsImage from "../../components/StaticGoogleMapsImage";
import LocalizedContent from "../../components/LocalizedContent";
// import OrderReviewNotice from "./OrderReviewNotice";
import ServiceDisplay from "./ServiceDisplay";
import ExtraServiceDisplay from "./ExtraServiceDisplay";
import ConfirmButton from "../../components/ConfirmButton";
import Timer from "../../components/Timer";
import BarberWaitTime from "../barbers/BarberWaitTime";
import ExtraServiceToggle from "./ExtraServiceToggle";
import placeholderAvatar from "../../assets/images/avatar-placeholder.png";

interface OrderReviewProps {
  onImageClick?: () => void;
  onComplete?: () => void;
}

const OrderReview: React.FC<OrderReviewProps> = ({
  onImageClick,
  onComplete,
}) => {
  const dispatch = useDispatch();

  const barbershop = useSelector(selectOrderBarbershop);
  const { coords, name, address, queue, extraServices } = barbershop || {};
  const selectedBarber = useSelector(selectOrderBarber);
  const selectedBarberId = selectedBarber?.id;

  const sending = useSelector(selectOrderSending);
  const orderServices = useSelector(selectOrderServices);
  const orderExtraServices = useSelector(selectOrderExtraServices);

  const recommendedExtraServices = useServiceExtraServices(
    orderServices[0]?.id,
    extraServices
  );

  const hasServices = !!(orderServices && orderServices.length);
  const hasExtraServices = !isEmptyObject(orderExtraServices);
  const hasRecommendedExtraServices = !!(
    recommendedExtraServices && recommendedExtraServices.length
  );

  /**
   * We want to allow recommendations only based on
   * the conditions when the review was mounted,
   * so we'll store it into ref.
   */
  const allowRecommendations = useRef(
    !hasExtraServices && hasRecommendedExtraServices
  );

  const footerRef = useRef<HTMLDivElement>(null);
  const isFooterOnScreen = useOnScreen(footerRef);

  useEffect(() => {
    if (sending === "fulfilled") {
      analytics.queueConfirmation(
        barbershop,
        orderServices,
        orderExtraServices
      );

      if (onComplete) {
        onComplete();
      }
    }
  }, [
    dispatch,
    sending,
    onComplete,
    orderServices,
    orderExtraServices,
    barbershop,
  ]);

  // Prefer selected barber's wait time, fallback to shop queue
  const selectedWaitTime = useMemo(() => {
    if (!barbershop || !barbershop.queues || !selectedBarberId) {
      return null as number | null;
    }
    const entry = barbershop.queues.find((q) => q && q.barber && q.barber.id === selectedBarberId);
    return typeof entry?.wait_time === "number" ? entry.wait_time : null;
  }, [barbershop, selectedBarberId]);

  const displayedWaitMinutes = useMemo(() => {
    return typeof selectedWaitTime === "number" ? selectedWaitTime : (queue as number | false);
  }, [selectedWaitTime, queue]);

  const barberFullName = selectedBarber
    ? selectedBarber.full_name || `${selectedBarber.firstname} ${selectedBarber.lastname}`
    : "";
  const barberShortName = useMemo(() => {
    if (!selectedBarber) return "";
    const first = selectedBarber.firstname || (selectedBarber.full_name ? selectedBarber.full_name.split(" ")[0] : "");
    const last = selectedBarber.lastname || (selectedBarber.full_name ? selectedBarber.full_name.split(" ").slice(-1)[0] : "");
    const lastInitial = last ? `${last.charAt(0).toUpperCase()}.` : "";
    return [first, lastInitial].filter(Boolean).join(" ").trim();
  }, [selectedBarber]);
  let resolvedImage: string | false = false;

  if (selectedBarber?.avatar_active && selectedBarber?.has_avatar && selectedBarber.avatar) {
    resolvedImage = selectedBarber.avatar;
  } else if (!selectedBarber?.avatar_active && selectedBarber?.has_image && selectedBarber.image) {
    resolvedImage = selectedBarber.image;
  }

  return (
    <div className="c-order-review">
      <div className="c-order-review__header">
        <StaticGoogleMapsImage
          className="c-order-review__image"
          coords={coords}
          onClick={onImageClick}
        >
          <div className="c-order-review__timer">
            <Timer minutes={displayedWaitMinutes} size="small" />
          </div>
        </StaticGoogleMapsImage>

        <div className="c-order-review__back">
          <button
            className="c-back-button"
            onClick={() => dispatch(editOrder())}
          >
            <Translate id="navigation.back" />
          </button>
        </div>
      </div>

      <div className="c-order-review__content">
        <div className="c-order-review__info">
          <h2 className="c-order-review__subtitle">
            <Translate id="orderReview.subtitle" />
          </h2>

          <LocalizedContent locales={["FI"]}>
            <p className="c-order-review__button-notice">
              <Translate id="barbershop.cashNotice" />
            </p>
          </LocalizedContent>

          <h1 className="c-order-review__title">{name}</h1>
          <p className="c-order-review__address">{address}</p>

          {selectedBarber && (
            <div className="c-order-review__barber">
              <div className="c-order-review-barber">
                <div className="c-order-review-barber__card">
                  <img
                    src={resolvedImage || placeholderAvatar}
                    alt={barberFullName}
                    className="c-order-review-barber__image"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.onerror = null;
                      target.src = placeholderAvatar;
                    }}
                  />
                  <span className="c-order-review-barber__label">{barberShortName}</span>
                  <div className="c-order-review-barber__wait">
                    <BarberWaitTime queue={(displayedWaitMinutes as number) || 0} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* This has been disabled for now, let's show it as a standalone modal before review instead. */}
          {/* <OrderReviewNotice className="c-order-review__notice" queue={queue} /> */}
        </div>

        {hasServices && (
          <div className="c-order-review__services">
            {orderServices.map((service) => (
              <ServiceDisplay
                {...service}
                key={service.id}
                data={orderExtraServices}
                render={(serviceExtraServices) =>
                  serviceExtraServices.map((extraService) => (
                    <ExtraServiceDisplay
                      key={extraService.id}
                      {...extraService}
                    />
                  ))
                }
              />
            ))}
          </div>
        )}

        {allowRecommendations.current && (
          <div className="c-order-review__recommendations">
            <h2 className="c-order-review__subtitle c-order-review__subtitle--recommendations">
              <Translate id="tracking.recommendationsTitle" />
            </h2>

            {recommendedExtraServices.map((extraService) => (
              <ExtraServiceToggle key={extraService.id} {...extraService} />
            ))}
          </div>
        )}
      </div>

      <div className="c-order-review__footer" ref={footerRef}>
        <ConfirmButton
          text={<Translate id="orderReview.cta" />}
          loading={sending}
          onClick={() => dispatch(sendOrder())}
        />
      </div>

      <AnimatePresence>
        {!isFooterOnScreen && (
          <FadeIn className="c-order-review__footer-arrow">
            <IconDownIndicator />
          </FadeIn>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrderReview;
